import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { LogoPanel } from "../components/studio/LogoPanel";
import { StillPanel } from "../components/studio/StillPanel";
import { VideoPanel } from "../components/studio/VideoPanel";
import { PreviewStage } from "../components/studio/PreviewStage";
import { Downloads } from "../components/studio/Downloads";
import { History } from "../components/studio/History";

const busyVideo = (v) => !["done", "failed"].includes(v.status);

export default function Studio() {
  const [logo, setLogo] = useState(null);
  const [stills, setStills] = useState([]);
  const [videos, setVideos] = useState([]);
  const [approvedId, setApprovedId] = useState(null);
  const [viewStillId, setViewStillId] = useState(null);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const prev = useRef({});

  const refresh = useCallback(async () => {
    const [s, v] = await Promise.all([api.stills(), api.videos()]);
    s.concat(v).forEach((item) => {
      const was = prev.current[item.id];
      if (was && was !== item.status) {
        if (item.status === "failed") toast.error(item.error || "Generation failed");
        if (item.status === "done") toast.success(item.still_id ? "Spinning video ready" : "Chrome still ready");
      }
      prev.current[item.id] = item.status;
    });
    setStills(s);
    setVideos(v);
    return { s, v };
  }, []);

  useEffect(() => {
    api.currentLogo().then(setLogo).catch(() => toast.error("Could not load your logo"));
    refresh().then(({ s, v }) => {
      const firstStill = s.find((x) => x.status === "done");
      if (firstStill) setViewStillId(firstStill.id);
      const firstVid = v.find((x) => x.status === "done");
      if (firstVid) setActiveVideoId(firstVid.id);
    });
  }, [refresh]);

  const anyBusy = stills.some((s) => s.status === "rendering") || videos.some(busyVideo);
  useEffect(() => {
    if (!anyBusy) return;
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [anyBusy, refresh]);

  const viewStill = stills.find((s) => s.id === viewStillId) || stills[0];
  const activeVideo = videos.find((v) => v.id === activeVideoId) || null;
  const renderingVideo = useMemo(() => videos.find(busyVideo), [videos]);

  const makeStill = async () => {
    try {
      const s = await api.makeStill(logo.id);
      prev.current[s.id] = s.status;
      setViewStillId(s.id);
      setApprovedId(null);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const makeVideo = async (duration) => {
    try {
      const v = await api.makeVideo(approvedId, duration);
      prev.current[v.id] = v.status;
      setActiveVideoId(v.id);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const stageMode = activeVideo ? "video" : viewStill?.status === "done" ? "still" : "logo";

  return (
    <div className="studio-grid grain min-h-screen">
      <header className="flex items-center justify-between px-6 lg:px-12 py-6 border-b border-[#1e2638]">
        <div className="flex items-baseline gap-4">
          <h1 data-testid="app-title" className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">
            <span className="chrome-text">OOshots</span> <span className="text-gray-400 font-medium">chrome loop studio</span>
          </h1>
        </div>
        <div data-testid="system-status" className="font-mono-ui text-xs uppercase tracking-widest text-gray-400 flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${anyBusy ? "bg-[#00ff87] pulse-dot" : "bg-gray-600"}`} />
          {anyBusy ? "Rendering" : "Idle"}
        </div>
      </header>

      <main className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-8 lg:gap-12 px-6 lg:px-12 py-10">
        <aside className="space-y-6 order-2 xl:order-1">
          <LogoPanel logo={logo} onLogo={setLogo} />
          <StillPanel
            logo={logo}
            still={viewStill}
            approved={approvedId && approvedId === viewStill?.id}
            onMake={makeStill}
            onApprove={() => setApprovedId(viewStill.id)}
          />
          <VideoPanel approvedId={approvedId} rendering={renderingVideo} onMake={makeVideo} />
        </aside>

        <section className="space-y-8 order-1 xl:order-2 min-w-0">
          <PreviewStage mode={stageMode} logo={logo} still={viewStill} video={activeVideo} />
          <Downloads video={activeVideo} />
          <History
            stills={stills}
            videos={videos}
            activeVideoId={activeVideo?.id}
            viewStillId={viewStill?.id}
            onPickStill={(id) => { setViewStillId(id); setActiveVideoId(null); }}
            onPickVideo={setActiveVideoId}
          />
        </section>
      </main>
    </div>
  );
}
